/**
 * Saving games to a file and restoring them.
 *
 * Every game lives only in the browser storage (IndexedDB) of the device that
 * ran it — if that phone dies, is replaced, or has its browser data cleared,
 * the games on it are gone. A backup file is the way out: "Save All Games"
 * writes them to a plain JSON file the person can keep anywhere (share sheet,
 * Drive, email), and "Restore" reads one back.
 *
 * A restored file is UNTRUSTED input: it goes straight into storage and is then
 * drawn on screen (a photo becomes an <img src>, a tile becomes an image path),
 * so `parseBackup` validates every field it will later use and rejects anything
 * odd rather than trying to repair it. It never overwrites a game the device
 * already has — see `planImport`.
 *
 * Pure (no browser APIs), so it is unit tested; the file/share plumbing is in
 * `src/ui/backupFile.ts`.
 */

import { getRuleset } from '../rules';
import { isBonusTile, isTile } from '../rules/tiles';
import { summarise } from './summary';
import type { Game } from './types';

export const BACKUP_APP = 'mahjong-scorer';
export const BACKUP_VERSION = 1;

const MAX_GAMES = 500;
const MAX_ROUNDS = 500;
const MAX_PHOTOS_PER_ROUND = 5;
const MAX_TEXT = 200;
const WINDS = ['east', 'south', 'west', 'north'];
const MELD_TYPES = ['chow', 'pung', 'kong', 'pair'];
/** Only image data URLs the app itself produces (downscaled JPEGs) — never an arbitrary URL. */
const PHOTO = /^data:image\/(jpeg|png|webp);base64,[A-Za-z0-9+/]+={0,2}$/;

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
/** A short, plain string: no angle brackets, so it can never be mistaken for markup if a screen forgets to escape it. */
const isText = (v: unknown, allowEmpty = false): v is string =>
  typeof v === 'string' && v.length <= MAX_TEXT && (allowEmpty || v.length > 0) && !/[<>]/.test(v);

export interface BackupFile {
  app: typeof BACKUP_APP;
  version: number;
  exportedAt: number;
  games: Game[];
}

/** The file's contents for these games. */
export function buildBackup(games: Game[], now: number = Date.now()): string {
  const file: BackupFile = { app: BACKUP_APP, version: BACKUP_VERSION, exportedAt: now, games };
  return JSON.stringify(file);
}

const pad = (n: number) => String(n).padStart(2, '0');

/** e.g. "mahjong-game-table-3-2026-09-20.json" or "mahjong-games-backup-2026-09-20.json". */
export function backupFileName(games: Game[], now: number = Date.now()): string {
  const d = new Date(now);
  const day = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  if (games.length === 1) {
    const table = games[0]!.tableId.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'game';
    return `mahjong-game-${table}-${day}.json`;
  }
  return `mahjong-games-backup-${day}.json`;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/** A round's edit trail (`RoundRecord.edited`): what it looked like before it was corrected. Untrusted like everything else. */
function validateEdited(e: unknown, at: string, playerIds: Set<string>): string | undefined {
  if (!isObj(e) || !isNum(e['at']) || !isNum(e['count']) || !Number.isInteger(e['count']) || e['count'] < 1 || e['count'] > 10_000) {
    return `${at} has a bad edit record.`;
  }
  const o = e['original'];
  if (!isObj(o)) return `${at} has a bad edit record.`;
  const knownPlayer = (v: unknown): boolean => typeof v === 'string' && playerIds.has(v);
  for (const key of ['winner', 'discarder'] as const) {
    if (o[key] !== undefined && !knownPlayer(o[key])) return `${at} has a bad edit record.`;
  }
  if (o['selfDraw'] !== undefined && typeof o['selfDraw'] !== 'boolean') return `${at} has a bad edit record.`;
  if (o['isLimit'] !== undefined && typeof o['isLimit'] !== 'boolean') return `${at} has a bad edit record.`;
  for (const key of ['fan', 'effectiveFan'] as const) {
    if (o[key] !== undefined && !isNum(o[key])) return `${at} has a bad edit record.`;
  }
  if (o['tenpai'] !== undefined && (!Array.isArray(o['tenpai']) || !o['tenpai'].every(knownPlayer))) {
    return `${at} has a bad edit record.`;
  }
  if (!Array.isArray(o['payments'])) return `${at} has a bad edit record.`;
  for (const p of o['payments']) {
    if (!isObj(p) || !knownPlayer(p['player']) || !isNum(p['delta'])) return `${at} has a bad edit record.`;
  }
  return undefined;
}

function validateRound(r: unknown, position: number, playerIds: Set<string>): string | undefined {
  const at = `Round ${position}`;
  if (!isObj(r)) return `${at} is not a round.`;
  if (r['index'] !== position) return `${at} is out of order.`;
  if (r['outcome'] !== 'win' && r['outcome'] !== 'draw') return `${at} has no outcome.`;
  if (typeof r['dealer'] !== 'string' || !playerIds.has(r['dealer'])) return `${at} has an unknown dealer.`;
  if (typeof r['prevailingWind'] !== 'string' || !WINDS.includes(r['prevailingWind'])) return `${at} has no prevailing wind.`;
  if (!isNum(r['recordedAt'])) return `${at} has no time.`;

  if (!Array.isArray(r['payments'])) return `${at} has no payments.`;
  for (const p of r['payments']) {
    if (!isObj(p) || typeof p['player'] !== 'string' || !playerIds.has(p['player']) || !isNum(p['delta'])) {
      return `${at} has a bad payment.`;
    }
  }

  if (r['outcome'] === 'win') {
    if (typeof r['winner'] !== 'string' || !playerIds.has(r['winner'])) return `${at} has an unknown winner.`;
  }
  for (const key of ['discarder'] as const) {
    if (r[key] !== undefined && (typeof r[key] !== 'string' || !playerIds.has(r[key] as string))) return `${at} has an unknown ${key}.`;
  }
  if (r['tenpai'] !== undefined) {
    if (!Array.isArray(r['tenpai']) || r['tenpai'].some((id) => typeof id !== 'string' || !playerIds.has(id))) {
      return `${at} has a bad tenpai list.`;
    }
  }
  for (const key of ['selfDraw', 'isLimit', 'claimedSet'] as const) {
    if (r[key] !== undefined && typeof r[key] !== 'boolean') return `${at} has a bad ${key}.`;
  }
  for (const key of ['fan', 'effectiveFan', 'basePoints'] as const) {
    if (r[key] !== undefined && !isNum(r[key])) return `${at} has a bad ${key}.`;
  }
  if (r['winningTile'] !== undefined && (typeof r['winningTile'] !== 'string' || !isTile(r['winningTile']))) {
    return `${at} has a bad winning tile.`;
  }

  if (r['patterns'] !== undefined) {
    if (!Array.isArray(r['patterns'])) return `${at} has bad patterns.`;
    for (const p of r['patterns']) {
      if (!isObj(p) || !isText(p['key']) || !isText(p['label']) || !isNum(p['fan'])) return `${at} has a bad pattern.`;
    }
  }
  if (r['concealed'] !== undefined) {
    if (!Array.isArray(r['concealed']) || r['concealed'].some((t) => typeof t !== 'string' || !isTile(t))) {
      return `${at} has bad hand tiles.`;
    }
  }
  if (r['melds'] !== undefined) {
    if (!Array.isArray(r['melds'])) return `${at} has bad melds.`;
    for (const m of r['melds']) {
      if (
        !isObj(m) ||
        typeof m['type'] !== 'string' ||
        !MELD_TYPES.includes(m['type']) ||
        typeof m['concealed'] !== 'boolean' ||
        !Array.isArray(m['tiles']) ||
        m['tiles'].some((t) => typeof t !== 'string' || !isTile(t))
      ) {
        return `${at} has a bad meld.`;
      }
    }
  }
  if (r['flowers'] !== undefined) {
    if (!Array.isArray(r['flowers']) || r['flowers'].some((t) => typeof t !== 'string' || !isBonusTile(t))) {
      return `${at} has bad flowers.`;
    }
  }
  if (r['photos'] !== undefined) {
    if (!Array.isArray(r['photos']) || r['photos'].length > MAX_PHOTOS_PER_ROUND || r['photos'].some((p) => typeof p !== 'string' || !PHOTO.test(p))) {
      return `${at} has a bad photo.`;
    }
  }
  if (r['edited'] !== undefined) return validateEdited(r['edited'], at, playerIds);
  return undefined;
}

/**
 * Does `imported` look like settings for a ruleset whose defaults are `defaults`?
 * Ruleset-agnostic on purpose: every TOP-level key the defaults have must be
 * present with the same kind of value (a mode string, a fan table object...),
 * and a nested value that is present must be the same type. A nested value that
 * is missing is fine — a game saved before a setting existed simply lacks it,
 * and the engine falls back to its default.
 */
export function settingsLookRight(imported: unknown, defaults: unknown, top = true): boolean {
  if (!isObj(defaults)) return typeof imported === typeof defaults;
  if (!isObj(imported)) return false;
  for (const [key, def] of Object.entries(defaults)) {
    const value = imported[key];
    if (value === undefined) {
      if (top) return false;
      continue;
    }
    if (!settingsLookRight(value, def, false)) return false;
  }
  return true;
}

/** A Leaderboard-mode game's extra record (see `LeaderboardInfo`): every field present and of the right kind. */
function leaderboardLooksRight(lb: unknown): boolean {
  if (!isObj(lb)) return false;
  if (!isText(lb['gameId']) || !isText(lb['periodId']) || !isText(lb['venue'])) return false;
  if (lb['settlement'] !== 'zero_sum' && lb['settlement'] !== 'winner_only') return false;
  if (!isNum(lb['startedAt'])) return false;
  if (lb['submittedAt'] !== undefined && !isNum(lb['submittedAt'])) return false;
  const rejected = lb['rejected'];
  if (rejected !== undefined && !(isObj(rejected) && isText(rejected['code']) && isText(rejected['message']))) return false;
  const seats = lb['notCounted'];
  return (
    Array.isArray(seats) &&
    seats.every(
      (s) => isObj(s) && isText(s['nickname']) && (s['reason'] === 'daily_limit' || s['reason'] === 'period_limit'),
    )
  );
}

/** A plain-English problem with `g`, or undefined if it is a game this app could have written. */
export function gameProblem(g: unknown): string | undefined {
  if (!isObj(g)) return 'not a game';
  if (!isText(g['id']) || !isText(g['tableId'])) return 'missing its id or table';
  if (typeof g['rulesetId'] !== 'string') return 'missing its ruleset';
  let defaults: unknown;
  try {
    defaults = getRuleset(g['rulesetId']).defaultSettings;
  } catch {
    return `uses a ruleset this app doesn't have ("${String(g['rulesetId']).slice(0, 30)}")`;
  }
  if (!settingsLookRight(g['settings'], defaults)) return 'has unreadable house rules';
  if (!isNum(g['startedAt'])) return 'has no start time';
  if (g['endedAt'] !== undefined && !isNum(g['endedAt'])) return 'has a bad end time';
  for (const key of ['seatingByDice', 'seatingPending'] as const) {
    if (g[key] !== undefined && typeof g[key] !== 'boolean') return `has a bad ${key}`;
  }
  if (g['leaderboard'] !== undefined && !leaderboardLooksRight(g['leaderboard'])) return 'has unreadable leaderboard details';

  const players = g['players'];
  if (!Array.isArray(players) || players.length !== 4) return 'does not have exactly four players';
  const ids = new Set<string>();
  for (const p of players) {
    if (!isObj(p) || !isText(p['id']) || !isText(p['name']) || typeof p['seat'] !== 'string' || !WINDS.includes(p['seat'])) {
      return 'has a bad player';
    }
    ids.add(p['id']);
  }
  if (ids.size !== 4) return 'has two players with the same id';

  const rounds = g['rounds'];
  if (!Array.isArray(rounds) || rounds.length > MAX_ROUNDS) return 'has an unreadable round list';
  for (let i = 0; i < rounds.length; i++) {
    const problem = validateRound(rounds[i], i + 1, ids);
    // Round messages are full sentences; the caller adds its own full stop.
    if (problem) return (problem.charAt(0).toLowerCase() + problem.slice(1)).replace(/\.$/, '');
  }

  // Last check: the rest of the app must be able to read it (ranking, badges).
  try {
    summarise(g as unknown as Game);
  } catch {
    return "can't be summarised";
  }
  return undefined;
}

export type ParsedBackup =
  | { ok: true; games: Game[]; rejected: string[] }
  | { ok: false; error: string };

/** Reads a backup file's text. Valid games are returned; games that fail validation are listed in `rejected`. */
export function parseBackup(text: string): ParsedBackup {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return { ok: false, error: "This file isn't a Mahjong Scorer game file (it couldn't be read)." };
  }
  if (!isObj(data) || data['app'] !== BACKUP_APP || !Array.isArray(data['games'])) {
    return { ok: false, error: "This file isn't a Mahjong Scorer game file." };
  }
  if (!isNum(data['version']) || data['version'] > BACKUP_VERSION) {
    return { ok: false, error: 'This file was made by a newer version of the app. Update the app and try again.' };
  }
  const raw = data['games'] as unknown[];
  if (raw.length === 0) return { ok: false, error: 'This file has no games in it.' };
  if (raw.length > MAX_GAMES) return { ok: false, error: 'This file has too many games to restore.' };

  const games: Game[] = [];
  const rejected: string[] = [];
  raw.forEach((g, i) => {
    const problem = gameProblem(g);
    if (problem) {
      rejected.push(`Game ${i + 1} ${problem}.`);
      return;
    }
    const o = g as Obj;
    // Only the fields this app knows — nothing extra rides along into storage.
    games.push({
      id: o['id'] as string,
      tableId: o['tableId'] as string,
      rulesetId: o['rulesetId'] as string,
      settings: o['settings'],
      players: o['players'] as Game['players'],
      rounds: o['rounds'] as Game['rounds'],
      startedAt: o['startedAt'] as number,
      ...(o['endedAt'] !== undefined ? { endedAt: o['endedAt'] as number } : {}),
      ...(o['seatingByDice'] !== undefined ? { seatingByDice: o['seatingByDice'] as boolean } : {}),
      ...(o['seatingPending'] !== undefined ? { seatingPending: o['seatingPending'] as boolean } : {}),
      ...(o['leaderboard'] !== undefined ? { leaderboard: o['leaderboard'] as Game['leaderboard'] } : {}),
    });
  });
  if (games.length === 0) return { ok: false, error: `None of the games in this file could be restored. ${rejected[0] ?? ''}`.trim() };
  return { ok: true, games, rejected };
}

/**
 * Which games to write and which to leave alone. A game whose id this device
 * already has is NEVER overwritten — the device's copy may be newer (more
 * rounds) than the one in the file.
 */
export function planImport(existingIds: ReadonlySet<string>, games: Game[]): { toSave: Game[]; alreadyHave: number } {
  const toSave: Game[] = [];
  const seen = new Set<string>();
  let alreadyHave = 0;
  for (const g of games) {
    if (existingIds.has(g.id) || seen.has(g.id)) {
      alreadyHave++;
      continue;
    }
    seen.add(g.id);
    toSave.push(g);
  }
  return { toSave, alreadyHave };
}

/** The one-line result shown after a restore. */
export function describeImport(saved: number, alreadyHave: number, rejected: number): string {
  const parts: string[] = [];
  parts.push(saved > 0 ? `Restored ${saved} game${saved === 1 ? '' : 's'}.` : 'No games were restored.');
  if (alreadyHave > 0) parts.push(`${alreadyHave} ${alreadyHave === 1 ? 'was' : 'were'} already on this device, so left as they are.`);
  if (rejected > 0) parts.push(`${rejected} could not be read and ${rejected === 1 ? 'was' : 'were'} skipped.`);
  return parts.join(' ');
}
